'use client'

import { useState } from 'react'
import { Lock } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import cn from '@config/tailwind/cn'

type AchievementItem = RouterOutputs['achievement']['getAll'][number]

/** Either an available badge (BASIC grid) or the badge attached to an earned achievement. */
export type BadgeCardBadge =
  | RouterOutputs['achievement']['getAvailableBadges'][number]
  | AchievementItem['achievementBadge']

export interface BadgeCardProps {
  badge: BadgeCardBadge
  /** The earned achievement for this badge, or `null` when the badge is still locked. */
  item: AchievementItem | null
  /** Show the unclaimed notification dot on earned-but-unclaimed badges. Only the owner claims. @default true */
  showUnclaimedDot?: boolean
  onOpen: () => void
}

export function BadgeCard({ badge, item, showUnclaimedDot = true, onOpen }: BadgeCardProps) {
  const earned = item !== null
  const isUnclaimed = earned && item.claimedAt == null && showUnclaimedDot
  // Deleted media leaves a truthy-but-dead iconUrl; fall back to the placeholder on load error.
  const [iconFailed, setIconFailed] = useState(false)

  return (
    <div
      className={cn(
        'srd:relative srd:flex srd:size-[104px] srd:cursor-pointer srd:items-center srd:justify-center srd:rounded-[16px] srd:border srd:border-blue-70 srd:bg-neutral-white srd:p-1',
      )}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      role='button'
      tabIndex={0}
    >
      {isUnclaimed && (
        <span aria-hidden className='srd:absolute srd:-right-1 srd:-top-1 srd:flex srd:size-3.5'>
          <span className='srd:absolute srd:inline-flex srd:size-full srd:animate-ping srd:rounded-full srd:bg-error srd:opacity-75' />
          <span className='srd:relative srd:inline-flex srd:size-3.5 srd:rounded-full srd:border-2 srd:border-neutral-white srd:bg-error' />
        </span>
      )}

      {badge.iconUrl && !iconFailed ? (
        <img
          src={badge.iconUrl}
          alt={badge.title}
          draggable={false}
          onError={() => setIconFailed(true)}
          className={cn('srd:size-[88px] srd:object-contain', !earned && 'srd:opacity-40 srd:grayscale')}
        />
      ) : (
        <div
          className={cn(
            'srd:flex srd:size-[88px] srd:items-center srd:justify-center srd:rounded-full srd:bg-grey-20',
            !earned && 'srd:opacity-40',
          )}
        >
          <Lock className='srd:size-9 srd:text-grey-60' />
        </div>
      )}
    </div>
  )
}
